import React, {useMemo, useState} from "react";
import {BarChart3, ChevronDown, ChevronUp} from "lucide-react";
import {cn} from "../../utils/cn";
import Card from "../../components/ui/Card";
import Avatar from "../../components/ui/Avatar";
import {ASSET_TYPES, typeLabel} from "./assetMeta";

/**
 * Allocation analytics (MoM point 12) — operational metrics for assigned
 * assets, computed client-side from the full asset set the page already
 * fetches for the KPI strip. Dense, collapsible strip: per-device-type
 * allocation bars on the left, top assignees (with user IDs) on the right.
 */
export default function AllocationInsights({assets = []}) {
  const [openPanel, setOpenPanel] = useState(false);

  const metrics = useMemo(() => {
    const total = assets.length;
    const isAssigned = (a) =>
      (a.assignedToUserId ?? a.assignedTo) != null || (a.assetStatus || "").toUpperCase() === "ASSIGNED";
    const assigned = assets.filter(isAssigned);

    const byType = ASSET_TYPES.map(({value}) => {
      const ofType = assets.filter((a) => a.assetType === value);
      return {
        type: value,
        total: ofType.length,
        assigned: ofType.filter(isAssigned).length,
      };
    }).filter((t) => t.total > 0);

    const byUser = {};
    assigned.forEach((a) => {
      const uid = a.assignedToUserId ?? a.assignedTo;
      const name = a.assignedToUsername || a.assignedToFullName || a.assignedToEmail || (uid != null ? `User ${uid}` : null);
      if (uid == null && !name) return;
      const key = uid != null ? `id:${uid}` : `n:${name}`;
      if (!byUser[key]) byUser[key] = {uid, name, count: 0};
      byUser[key].count++;
    });
    const topAssignees = Object.values(byUser).sort((a, b) => b.count - a.count).slice(0, 5);

    return {total, assignedCount: assigned.length, byType, topAssignees};
  }, [assets]);

  if (!metrics.total) return null;

  const pct = metrics.total ? Math.round((metrics.assignedCount / metrics.total) * 100) : 0;

  const toggle = () => {
    const next = !openPanel;
    console.info("[Assets] Allocation insights", next ? "expanded" : "collapsed");
    if (next) console.debug("[Assets] Allocation metrics", metrics);
    setOpenPanel(next);
  };

  return (
    <Card>
      {/* Always-visible summary strip — one short row */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={openPanel}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
      >
        <BarChart3 className="h-4 w-4 text-brand-600 shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-ink-900 shrink-0">Allocation</span>
        <span className="text-xs text-ink-500 shrink-0">
          {metrics.assignedCount} of {metrics.total} assets assigned ({pct}%)
        </span>
        <span className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden min-w-[80px]" aria-hidden="true">
          <span className="block h-full bg-brand-600" style={{width: `${pct}%`}} />
        </span>
        {openPanel
          ? <ChevronUp className="h-4 w-4 text-ink-400 shrink-0" aria-hidden="true" />
          : <ChevronDown className="h-4 w-4 text-ink-400 shrink-0" aria-hidden="true" />}
      </button>

      {openPanel && (
        <div className="px-3 pb-3 pt-1 border-t border-ink-100 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {/* Per-device-type allocation */}
          <div>
            <p className="text-2xs uppercase tracking-wider font-semibold text-ink-500 mb-1.5">By device type</p>
            <ul className="space-y-1.5">
              {metrics.byType.map((t) => {
                const p = t.total ? Math.round((t.assigned / t.total) * 100) : 0;
                return (
                  <li key={t.type} className="flex items-center gap-2">
                    <span className="text-xs text-ink-700 w-24 shrink-0 truncate" title={typeLabel(t.type)}>{typeLabel(t.type)}</span>
                    <span className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden" aria-hidden="true">
                      <span className={cn("block h-full", p === 100 ? "bg-success-500" : "bg-info-500")} style={{width: `${p}%`}} />
                    </span>
                    <span className="text-xs tabular-nums text-ink-600 w-16 text-right shrink-0">{t.assigned}/{t.total}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Top assignees — explicit user IDs (MoM point 10 carried through) */}
          <div>
            <p className="text-2xs uppercase tracking-wider font-semibold text-ink-500 mb-1.5">Top assignees</p>
            {metrics.topAssignees.length === 0 ? (
              <p className="text-xs text-ink-400">No assets are assigned to users yet.</p>
            ) : (
              <ul className="space-y-1">
                {metrics.topAssignees.map((u) => (
                  <li key={`${u.uid}-${u.name}`} className="flex items-center gap-2 min-w-0">
                    <Avatar name={u.name || String(u.uid)} size="sm" />
                    <span className="text-xs text-ink-800 truncate min-w-0" title={u.name}>{u.name}</span>
                    {u.uid != null && <span className="text-2xs font-mono text-ink-400 shrink-0">ID {u.uid}</span>}
                    <span className="ml-auto text-xs tabular-nums font-semibold text-ink-700 shrink-0">
                      {u.count} asset{u.count === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
